import { type Statement } from 'launchdarkly-api-typescript';

import { type CustomRole, type Member } from '../../../model';

export type ConsolidationResult = {
  consolidatedRole: CustomRole;
  consolidationInfo: ConsolidationInfo;
};

export type ConsolidationInfo = {
  hasBaseRole: boolean;
  baseRole?: string;
  hasDirectCustomRoles: boolean;
  directCustomRoles: CustomRole[];
  hasTeamRoles: boolean;
  teamRoles: Array<{ role: CustomRole; teamKey: string; teamName: string }>;
  conflicts: ConflictInfo[];
  roleAttributeSources: RoleAttributeSource[];
  missingRoleAttributes?: string[];
  customRoleBaseRoles?: {
    hasBaseRoles: boolean;
    uniqueBaseRoles: string[];
    isConsistent: boolean;
    dominantBaseRole?: string;
    rolesByBaseRole: { [baseRole: string]: string[] };
  };
};

export type ConflictInfo = {
  type: 'override' | 'merge' | 'conflict';
  description: string;
  affectedResources?: string[];
};

export type RoleAttributeSource = {
  attributeName: string;
  values: string[];
  source: 'member' | 'team';
  teamKey?: string;
};

function mergeCustomRoles(roles: CustomRole[]): CustomRole[] {
  return roles;
}

export function getEffectiveCustomRolePermissions(role: CustomRole): Statement[] {
  const roleAny = role;
  const policies: Statement[] = [];
  if (roleAny.policy && Array.isArray(roleAny.policy)) {
    policies.push(...roleAny.policy);
  }

  if (roleAny._presetStatements && Array.isArray(roleAny._presetStatements)) {
    policies.push(...roleAny._presetStatements);
  }

  return policies;
}

function mergeRoleAttributes(
  member: Member,
  teamRolesData: Array<{ role: CustomRole; teamKey: string; teamName: string }>,
  teamRoleAttributes: { [teamKey: string]: { [attributeName: string]: string[] } },
  consolidationInfo: ConsolidationInfo,
): { [attributeName: string]: string[] } {
  const mergedAttributes: { [attributeName: string]: string[] } = {};

  teamRolesData.forEach(({ teamKey }) => {
    const teamAttributes = teamRoleAttributes[teamKey];
    if (teamAttributes) {
      Object.entries(teamAttributes).forEach(([attributeName, values]) => {
        if (!mergedAttributes[attributeName]) {
          mergedAttributes[attributeName] = [];
        }
        values.forEach((value) => {
          if (!mergedAttributes[attributeName].includes(value)) {
            mergedAttributes[attributeName].push(value);
          }
        });

        consolidationInfo.roleAttributeSources.push({
          attributeName,
          values,
          source: 'team',
          teamKey,
        });
      });
    }
  });

  if (member.roleAttributes) {
    Object.entries(member.roleAttributes).forEach(([attributeName, values]) => {
      if (!mergedAttributes[attributeName]) {
        mergedAttributes[attributeName] = [];
      }

      values.forEach((value) => {
        if (!mergedAttributes[attributeName].includes(value)) {
          mergedAttributes[attributeName].push(value);
        }
      });

      consolidationInfo.roleAttributeSources.push({
        attributeName,
        values,
        source: 'member',
      });
    });
  }

  return mergedAttributes;
}

function createBaseRoleAsCustomRole(baseRole: string): CustomRole {
  return {
    _id: `base-${baseRole}`,
    key: `base-${baseRole}`,
    name: `Base Role: ${baseRole}`,
    description: `Base ${baseRole} role permissions`,
    policy: [],
    _links: {}, // Required by type but not displayed
  } as CustomRole;
}

function createConsolidatedRole(
  roles: CustomRole[],
  mergedRoleAttributes: { [attributeName: string]: string[] },
  member: Member,
): CustomRole {
  const allPolicies = roles.flatMap((role) => getEffectiveCustomRolePermissions(role));

  return {
    _id: `consolidated-${member._id}`,
    key: `consolidated-${member._id}`,
    name: `Consolidated Permissions: ${member.email}`,
    description: `All effective permissions for ${member.email}`,
    policy: allPolicies,
    roleAttributes: mergedRoleAttributes,
    _links: {}, // Required by type but not displayed
  } as CustomRole;
}

export function consolidateMemberRoles(
  member: Member,
  directCustomRoles: CustomRole[],
  teamRolesData: Array<{ role: CustomRole; teamKey: string; teamName: string }>,
  teamRoleAttributes: { [teamKey: string]: { [attributeName: string]: string[] } },
): ConsolidationResult {
  const consolidationInfo: ConsolidationInfo = {
    hasBaseRole: !directCustomRoles.length,
    baseRole: member.role,
    hasDirectCustomRoles: directCustomRoles.length > 0,
    directCustomRoles,
    hasTeamRoles: teamRolesData.length > 0,
    teamRoles: teamRolesData,
    conflicts: [],
    roleAttributeSources: [],
  };

  let basePermissions: CustomRole[];

  if (directCustomRoles.length > 0) {
    if (directCustomRoles.length === 1) {
      basePermissions = directCustomRoles;
      consolidationInfo.conflicts.push({
        type: 'override',
        description: `Direct custom role "${directCustomRoles[0].name}" overrides base role "${member.role}"`,
      });
    } else {
      basePermissions = mergeCustomRoles(directCustomRoles);
      consolidationInfo.conflicts.push({
        type: 'merge',
        description: `${directCustomRoles.length} direct custom roles merged together (most permissive permissions win)`,
      });
    }
  } else {
    basePermissions = [createBaseRoleAsCustomRole(member.role)];
  }

  const teamRoles = teamRolesData.map(({ role }) => role);
  let finalPermissions: CustomRole[];

  if (teamRoles.length > 0) {
    finalPermissions = basePermissions.concat(teamRoles);
    consolidationInfo.conflicts.push({
      type: 'merge',
      description: `${teamRoles.length} team roles added to ${directCustomRoles.length ? 'direct custom roles' : 'base role'}`,
    });
  } else {
    finalPermissions = basePermissions;
  }

  const mergedRoleAttributes = mergeRoleAttributes(member, teamRolesData, teamRoleAttributes, consolidationInfo);

  const consolidatedRole = createConsolidatedRole(finalPermissions, mergedRoleAttributes, member);

  return {
    consolidatedRole,
    consolidationInfo,
  };
}

export function extractRoleAttributePlaceholders(policy: Array<Statement | BaseRolePermission>): string[] {
  const placeholders = new Set<string>();
  policy.forEach((policyItem) => {
    if ('resources' in policyItem && policyItem.resources) {
      policyItem.resources.forEach((resource: string) => {
        const regex = /\$\{roleAttribute\/([^}]+)\}/g;
        let match;
        // oxlint-disable-next-line no-cond-assign
        while ((match = regex.exec(resource)) !== null) {
          placeholders.add(match[1]);
        }
      });
    }
  });

  return Array.from(placeholders);
}

export function findMissingRoleAttributes(
  policy: Array<Statement | BaseRolePermission>,
  roleAttributes: { [attributeName: string]: string[] },
): string[] {
  const requiredAttributes = extractRoleAttributePlaceholders(policy);

  return requiredAttributes.filter((attr) => !roleAttributes[attr] || roleAttributes[attr].length === 0);
}

export function groupSimilarPolicies(policies: Statement[]): Statement[] {
  const resourceGroups = new Map<string, Statement>();

  policies.forEach((policy) => {
    const statementPolicy = policy;
    if (!statementPolicy.resources) {
      return;
    }

    statementPolicy.resources.forEach((resource: string) => {
      const effect = statementPolicy.effect || 'allow';
      const hasNotActions = !!statementPolicy.notActions;
      const actionType = hasNotActions ? 'notActions' : 'actions';
      const groupKey = `${resource}::${effect}::${actionType}`;

      if (resourceGroups.has(groupKey)) {
        const existingGroup = resourceGroups.get(groupKey)!;

        if (hasNotActions) {
          const currentNotActions = statementPolicy.notActions || [];
          currentNotActions.forEach((action: string) => {
            if (!existingGroup.notActions?.includes(action)) {
              if (!existingGroup.notActions) {
                existingGroup.notActions = [];
              }
              existingGroup.notActions.push(action);
            }
          });
        } else {
          const currentActions = statementPolicy.actions || [];
          currentActions.forEach((action: string) => {
            if (!existingGroup.actions?.includes(action)) {
              if (!existingGroup.actions) {
                existingGroup.actions = [];
              }
              existingGroup.actions.push(action);
            }
          });
        }
      } else {
        const newGroup: Statement = {
          resources: [resource],
          effect,
          ...(hasNotActions
            ? { notActions: statementPolicy.notActions ? statementPolicy.notActions.slice() : [] }
            : { actions: statementPolicy.actions ? statementPolicy.actions.slice() : [] }),
        };

        resourceGroups.set(groupKey, newGroup);
      }
    });
  });

  // Helper function for resource subsumption
  function isResourceSubsumedBy(specificResource: string, broaderResource: string): boolean {
    if (specificResource.includes('${roleAttribute/') || broaderResource.includes('${roleAttribute/')) {
      return false;
    }

    if (broaderResource.includes('*')) {
      const regexPattern = broaderResource
        .replace(/\*/g, '[^:]*') // * matches any characters except ':'
        .replace(/\//g, '\\/'); // Escape forward slashes

      const regex = new RegExp(`^${regexPattern}$`);
      return regex.test(specificResource);
    }

    return specificResource.startsWith(`${broaderResource}:`);
  }

  const finalGroups = new Map<string, Statement>();

  resourceGroups.forEach((group, groupKey) => {
    const [resource, effect, actionType] = groupKey.split('::');

    let isSubsumed = false;

    resourceGroups.forEach((otherGroup, otherGroupKey) => {
      const [otherResource, otherEffect, otherActionType] = otherGroupKey.split('::');

      // Skip if same resource, different effect, or different action type
      if (resource === otherResource || effect !== otherEffect || actionType !== otherActionType) {
        return;
      }

      if (isResourceSubsumedBy(resource, otherResource)) {
        let allActionsCovered = false;

        if (actionType === 'notActions') {
          const currentNotActions = group.notActions || [];
          const otherNotActions = otherGroup.notActions || [];

          allActionsCovered = currentNotActions.every((action: string) => otherNotActions.includes(action));
        } else {
          const currentActions = group.actions || [];
          const otherActions = otherGroup.actions || [];

          allActionsCovered = currentActions.every((action: string) => otherActions.includes(action));
        }

        if (allActionsCovered) {
          isSubsumed = true;
        }
      }
    });

    if (!isSubsumed) {
      finalGroups.set(groupKey, group);
    }
  });

  return Array.from(finalGroups.values());
}

type BaseRolePermission = {
  _isBaseRolePermission: boolean;
  baseRoleType: string;
  description: string;
};
